import { api } from './api';
import {
  AuthResponse,
  LoginPayload,
  RegisterPayload,
  TwoFactorChallengePayload,
  TwoFactorDisablePayload,
  TwoFactorPayload,
  TwoFactorSetupResponse,
  User,
} from '../types/api';

export const authService = {
  register: async (payload: RegisterPayload): Promise<AuthResponse> => {
    const { data } = await api.post<AuthResponse>('/auth/register', payload);
    return data;
  },

  login: async (payload: LoginPayload): Promise<AuthResponse> => {
    const { data } = await api.post<AuthResponse>('/auth/login', payload);
    return data;
  },

  /* ----- Double authentification (TOTP) ----- */

  /**
   * Deuxième étape de connexion : `POST /auth/login` a répondu 401
   * `AUTH_2FA_REQUIRED` avec un `challengeToken` (valable ~5 min). Même réponse
   * que le login (user + accessToken ; refresh token en cookie httpOnly).
   */
  verifyTwoFactorChallenge: async (payload: TwoFactorChallengePayload): Promise<AuthResponse> => {
    const { data } = await api.post<AuthResponse>('/auth/2fa/challenge', payload);
    return data;
  },

  /** Génère le secret TOTP (authentifié) — la 2FA n'est active qu'après `enableTwoFactor`. */
  setupTwoFactor: async (): Promise<TwoFactorSetupResponse> => {
    const { data } = await api.post<TwoFactorSetupResponse>('/auth/2fa/setup');
    return data;
  },

  /**
   * Active la 2FA en validant un premier code issu de l'application
   * d'authentification (authentifié, après `setupTwoFactor`).
   */
  verifyTwoFactor: async (payload: TwoFactorPayload): Promise<{ ok?: boolean }> => {
    const { data } = await api.post<{ ok?: boolean }>('/auth/2fa/verify', payload);
    return data ?? {};
  },

  /** Alias lisible de `verifyTwoFactor` (activation). */
  enableTwoFactor: async (code: string): Promise<{ ok?: boolean }> => {
    const { data } = await api.post<{ ok?: boolean }>('/auth/2fa/verify', { code });
    return data ?? {};
  },

  /** Désactive la 2FA (mot de passe obligatoire, code TOTP optionnel). */
  disableTwoFactor: async (password: string, code?: string): Promise<{ ok?: boolean }> => {
    const payload: TwoFactorDisablePayload = code ? { password, code } : { password };
    const { data } = await api.post<{ ok?: boolean }>('/auth/2fa/disable', payload);
    return data ?? {};
  },

  logout: async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } catch {
      // best-effort
    }
  },

  me: async (): Promise<User> => {
    const { data } = await api.get<User>('/users/me');
    return data;
  },

  forgotPassword: async (identifier: string): Promise<{ ok: boolean }> => {
    const { data } = await api.post<{ ok: boolean }>('/auth/forgot', { identifier });
    return data;
  },

  resetPassword: async (token: string, password: string): Promise<{ ok: boolean }> => {
    const { data } = await api.post<{ ok: boolean }>('/auth/reset', { token, password });
    return data;
  },

  verifyEmail: async (token: string): Promise<{ ok: boolean }> => {
    const { data } = await api.post<{ ok: boolean }>('/auth/verify-email', { token });
    return data;
  },

  resendVerification: async (email: string): Promise<{ ok: boolean }> => {
    const { data } = await api.post<{ ok: boolean }>('/auth/resend-verification', { email });
    return data;
  },
};
